import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import {
  fetchCaseMailThreadCandidates,
  postCaseLinkMailThread,
} from "../../../api/case";
import { ResourceLoading } from "../../utils/ResourceLoading";

export function CaseLinkMailThreadDialog({ open, onClose, caseId, onLinked }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !caseId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await fetchCaseMailThreadCandidates(caseId);
      if (cancelled) return;
      if (res?.__error || res?.errors) {
        setCandidates([]);
        setError(res?.errors?.[0] || t("case.mail_thread.candidates_failed"));
        setLoading(false);
        return;
      }
      setCandidates(Array.isArray(res?.data) ? res.data : []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, caseId, t]);

  const handleLink = async (row) => {
    setSaving(true);
    const res = await postCaseLinkMailThread(caseId, {
      conversation_id: row.conversation_id,
      mailbox_id: row.mailbox_id,
      graph_message_id: row.graph_message_id,
    });
    setSaving(false);
    if (res?.__error || res?.errors) {
      toast.error(res?.errors?.[0] || t("case.mail_thread.link_failed"));
      return;
    }
    toast.success(t("case.mail_thread.linked"));
    onLinked?.(res);
    onClose?.();
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title="case.mail_thread.link_title"
      size="2xl"
      closeOnOutsideClick
    >
      {loading ? (
        <ResourceLoading />
      ) : error ? (
        <p className="text-sm text-rose-600">{error}</p>
      ) : candidates.length === 0 ? (
        <p className="text-sm text-slate-500">{t("case.mail_thread.no_candidates")}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {candidates.map((row) => (
            <li key={`${row.mailbox_id}-${row.graph_message_id}`} className="flex items-start justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">
                  {row.subject || t("E-post")}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {[row.from_name, row.from_email, row.mailbox_email]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <button
                type="button"
                disabled={saving}
                className="shrink-0 rounded-lg bg-[#1463C7] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                onClick={() => handleLink(row)}
              >
                {t("case.mail_thread.link_action")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </PMDialog>
  );
}
